import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from 'use-toast-mui';
import { useQuery } from '@tanstack/react-query';
import { TableBody } from '@mui/material';
import qs from 'qs';

import AnimationPage from '../../../pages/AnimationPage';
import CommonLoading from '../../../form-components/CommonLoading';
import CommonPagination from '../../../form-components/CommonPagination';
import CommonSelect from '../../../form-components/CommonSelect';
import { CommonExcelButton } from '../../../form-components/CommonExcelButton';
import { getExpireUpdateHistory, getStopHistory } from '../../../api/member-course-history';

import {
  BodyCell,
  BodyRow,
  Card,
  EmptyCell,
  FilterBar,
  FilterGroup,
  FilterLabel,
  HeaderCell,
  StyledTable,
  StyledTableContainer,
  StyledTableHead,
  ToolbarRow,
  TotalCount,
} from './UserCourseHistoryStyled';

// 날짜 표시 — 'YYYY-MM-DD'까지만 노출
const toDate = value => (value ? String(value).substring(0, 10) : '-');

// 일시 표시 — 'YYYY-MM-DD HH:mm:ss'까지 노출 (실행일용)
const toDateTime = value => (value ? String(value).replace('T', ' ').substring(0, 19) : '-');

// 예약설정 제한값 표시 — null은 무제한
const toLimit = value => (value == null ? '무제한' : `${value}회`);

// 중지설정 일수 표시 — null은 무제한
const toLimitDays = value => (value == null ? '무제한' : `${value}일`);

// 자동 차감 설정 표시 — null은 미사용
const toAutoDeductionCount = value => (value == null ? '미사용' : `월 ${value}회`);
const toAutoDeductionAnchor = value => (value == null ? '미사용' : `매월 ${value}일`);

// 변경 전 → 변경 후 — actionType별로 만료일/시작일/잔여횟수를 분기해서 표시
// 이용지점 변경은 before/after 컬럼이 없어 REASON 한 줄이 곧 변경 내용이다.
// 그 경우 '메모'까지 같은 문구를 반복하지 않는다. 공유 추가·해제, 양도/양도 취소도 reason(상대방 이름·전화)이 곧 변경 내용이다.
const REASON_AS_CONTENT_TYPES = ['USE_BRANCH_CHANGE', 'SHARE_ADD', 'SHARE_REMOVE', 'TRANSFERRED', 'TRANSFER_CANCELED'];
export const toMemo = row => (REASON_AS_CONTENT_TYPES.includes(row?.actionType) ? '-' : row?.reason ?? '-');

export const toChangeContent = row => {
  switch (row?.actionType) {
    case 'USAGE_COUNT_CHANGE':
    case 'TOTAL_COUNT_CHANGE':
    case 'AUTO_DEDUCTION':
      return `${row?.beforeUsageCount ?? '-'}회 → ${row?.afterUsageCount ?? '-'}회`;
    case 'DAILY_LIMIT_CHANGE':
    case 'WEEKLY_LIMIT_CHANGE':
    case 'RESERVATION_LIMIT_CHANGE':
    case 'CANCEL_LIMIT_CHANGE':
      return `${toLimit(row?.beforeUsageCount)} → ${toLimit(row?.afterUsageCount)}`;
    case 'STOP_COUNT_CHANGE':
      return `${toLimit(row?.beforeUsageCount)} → ${toLimit(row?.afterUsageCount)}`;
    case 'STOP_PERIOD_CHANGE':
      return `${toLimitDays(row?.beforeUsageCount)} → ${toLimitDays(row?.afterUsageCount)}`;
    case 'AUTO_DEDUCTION_COUNT_CHANGE':
      return `${toAutoDeductionCount(row?.beforeUsageCount)} → ${toAutoDeductionCount(row?.afterUsageCount)}`;
    case 'AUTO_DEDUCTION_ANCHOR_CHANGE':
      return `${toAutoDeductionAnchor(row?.beforeUsageCount)} → ${toAutoDeductionAnchor(row?.afterUsageCount)}`;
    case 'START_DATE_CHANGE':
      return `${toDate(row?.beforeStartDate)} → ${toDate(row?.afterStartDate)}`;
    case 'USE_BRANCH_CHANGE':
      // 이용지점은 before/after 컬럼이 없어 reason("이용지점: A, B → A")에 한 줄로 남긴다
      return row?.reason?.replace(/^이용지점: /, '') ?? '-';
    // 양도(#190)는 "상대에게서 양도\n횟수 · 기간 (시작 ~ 만료)" — 기간 괄호를 한 줄 더 내려 세 줄로 보인다
    case 'TRANSFERRED':
      return row?.reason?.replace(/ \((?=\d{4}-\d{2}-\d{2} ~ )/, '\n(') ?? '-';
    // 수강권 공유(#154)·양도 취소 — reason 에 상대 회원 "이름(전화번호)"
    case 'SHARE_ADD':
    case 'SHARE_REMOVE':
    case 'TRANSFER_CANCELED':
      return row?.reason ?? '-';
    default:
      // EXPIRE_DATE_EXTENSION / REDUCTION / UPDATE
      return `${toDate(row?.beforeExpireDate)} → ${toDate(row?.afterExpireDate)}`;
  }
};

// 연장일 표시 — 일괄 연장은 extensionDays, 단건 만료일 수정은 before/after 만료일 차이로 계산
const toExtensionDays = row => {
  if (row?.extensionDays != null) return `${row.extensionDays}일`;
  if (row?.beforeExpireDate && row?.afterExpireDate) {
    const days = Math.round(
      (new Date(row.afterExpireDate) - new Date(row.beforeExpireDate)) / 86400000,
    );
    return `${days}일`;
  }
  return '-';
};

// 상품 구분(category) 라벨
const CATEGORY_LABELS = { TICKET: '회원권', PASS: '패스', TRIAL: '일일권', LOCKER: '락커', PRODUCT: '일반상품' };

// 중지 내역 actionType 라벨
export const STOP_ACTION_LABELS = { STOPPED: '중지', STOP_CANCELED: '중지해제', STOP_MODIFIED: '중지수정' };

// 정보수정 내역 actionType 라벨
export const EXPIRE_ACTION_LABELS = {
  EXPIRE_DATE_EXTENSION: '만료일 연장',
  EXPIRE_DATE_REDUCTION: '만료일 축소',
  EXPIRE_DATE_UPDATE: '만료일 수정',
  START_DATE_CHANGE: '시작일 변경',
  USAGE_COUNT_CHANGE: '잔여횟수 변경',
  TOTAL_COUNT_CHANGE: '총 횟수 변경',
  AUTO_DEDUCTION: '자동 차감',
  DAILY_LIMIT_CHANGE: '일일 이용 제한 변경',
  WEEKLY_LIMIT_CHANGE: '주간 이용 제한 변경',
  RESERVATION_LIMIT_CHANGE: '동시 예약 횟수 변경',
  CANCEL_LIMIT_CHANGE: '취소 가능 횟수 변경',
  STOP_COUNT_CHANGE: '중지 가능 횟수 변경',
  STOP_PERIOD_CHANGE: '중지 가능 일수 변경',
  USE_BRANCH_CHANGE: '이용지점 변경',
  SHARE_ADD: '공유 추가',
  SHARE_REMOVE: '공유 해제',
  AUTO_DEDUCTION_COUNT_CHANGE: '자동 차감 횟수 변경',
  AUTO_DEDUCTION_ANCHOR_CHANGE: '자동 차감 기준일 변경',
  TRANSFERRED: '양도',
  TRANSFER_CANCELED: '양도 취소',
};

// 페이지 개수 옵션 (스크린샷의 "30개씩")
const PAGE_SIZE_OPTIONS = [
  { value: 10, label: '10개씩' },
  { value: 20, label: '20개씩' },
  { value: 30, label: '30개씩' },
  { value: 50, label: '50개씩' },
];

// 정보수정 내역 컬럼 (만료일/시작일/잔여횟수 변경 — actionType별로 표시 분기)
const EXPIRE_COLUMNS = [
  { label: '변경 종류', render: row => EXPIRE_ACTION_LABELS[row?.actionType] ?? '-' },
  { label: '구분', render: row => CATEGORY_LABELS[row?.category] ?? '-' },
  { label: '상품', render: row => row?.productName ?? '-' },
  // 양도(#190)는 여러 줄이라 줄바꿈을 살리고, 숫자·날짜가 중간에서 끊기지 않게 단어 단위로 접는다
  { label: '변경 전 → 변경 후', render: toChangeContent, wrap: true, preLine: true },
  { label: '연장일', render: toExtensionDays },
  { label: '메모', render: toMemo, wrap: true },
  { label: '관리 담당자', render: row => row?.regName ?? '-' },
  // 처리지점 — 다지점에서 어느 지점 직원이 처리했는지. 과거 데이터는 값이 없어 '-'.
  { label: '처리지점', render: row => row?.processedBranchName ?? '-' },
  { label: '실행일', render: row => toDateTime(row?.regDt) },
];

// 상품중지 내역 컬럼
const STOP_COLUMNS = [
  { label: '실행일', render: row => toDateTime(row?.regDt) },
  { label: '구분', render: row => STOP_ACTION_LABELS[row?.actionType] ?? '-' },
  { label: '상품', render: row => row?.productName ?? '-' },
  { label: '중지일', render: row => toDate(row?.stoppedDate) },
  { label: '중지해제일', render: row => toDate(row?.stoppedEndDate) },
  { label: '중지일수', render: row => (row?.stoppedDays != null ? `${row.stoppedDays}일` : '-') },
  { label: '메모', render: row => row?.stopMemo ?? '-', wrap: true },
  { label: '관리 담당자', render: row => row?.regName ?? '-' },
  { label: '처리지점', render: row => row?.processedBranchName ?? '-' },
];

// 회원 단위 내역은 백엔드가 전량을 메모리에 올려 페이징하므로, 한 번에 모두 받아 화면에서 필터링한다.
// (상품/변경종류 드롭다운 옵션과 건수를 정확히 만들려면 현재 페이지가 아닌 전체 데이터가 필요)
const FETCH_ALL_LIMIT = 1000;

// 탭별 설정 (만료일연장 / 상품중지 차이를 한 곳에 모아둔다)
const HISTORY_CONFIG = {
  EXPIRE: {
    queryKey: 'memberExpireHistory',
    fetchList: getExpireUpdateHistory,
    columns: EXPIRE_COLUMNS,
    actionLabels: EXPIRE_ACTION_LABELS,
  },
  STOP: {
    queryKey: 'memberStopHistory',
    fetchList: getStopHistory,
    columns: STOP_COLUMNS,
    actionLabels: STOP_ACTION_LABELS,
  },
};

const UserCourseHistoryTable = ({ type, user, bigUser }) => {
  const config = HISTORY_CONFIG[type];
  const { t } = useTranslation();
  const toast = useToast();

  const token = bigUser?.token;
  const memberId = user?.memberId;
  const branchId = bigUser?.branch?.branchId;

  // 상품 / 변경종류 드롭다운 필터 + 클라이언트 페이징 상태
  const [productFilter, setProductFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(30);

  const { data: list, isLoading } = useQuery({
    queryKey: [config.queryKey, branchId, memberId],
    queryFn: () =>
      config.fetchList(qs.stringify({ branchId, memberId, page: 1, limit: FETCH_ALL_LIMIT }), token),
    enabled: !!token && !!branchId,
  });

  const allRows = list?.content ?? [];

  // 탭(만료일연장 ↔ 상품중지) 전환 시 필터·페이지 초기화
  useEffect(() => {
    setProductFilter('');
    setActionFilter('');
    setPage(1);
  }, [type]);

  // 드롭다운 옵션 — 실제 내역에 존재하는 값만 노출
  const productOptions = useMemo(
    () =>
      [...new Set(allRows.map(row => row?.productName).filter(Boolean))].map(name => ({
        value: name,
        label: name,
      })),
    [allRows],
  );

  const actionOptions = useMemo(
    () =>
      [...new Set(allRows.map(row => row?.actionType).filter(Boolean))].map(actionType => ({
        value: actionType,
        label: config.actionLabels[actionType] ?? actionType,
      })),
    [allRows, config],
  );

  // 선택된 상품 / 변경종류로 필터링
  const filteredRows = useMemo(
    () =>
      allRows.filter(
        row =>
          (!productFilter || row?.productName === productFilter) &&
          (!actionFilter || row?.actionType === actionFilter),
      ),
    [allRows, productFilter, actionFilter],
  );

  const pagedRows = filteredRows.slice((page - 1) * pageSize, page * pageSize);

  const handleChangePage = (_, value) => setPage(value);

  const handleChangePageSize = e => {
    setPageSize(Number(e.target.value));
    setPage(1);
  };

  const handleChangeProduct = e => {
    setProductFilter(e.target.value);
    setPage(1);
  };

  const handleChangeAction = e => {
    setActionFilter(e.target.value);
    setPage(1);
  };

  // 엑셀 다운로드는 엑셀 스펙 확정 후 연결 (기존 코드 관례와 동일)
  const handleExcelDownload = () => {
    toast.info(t('준비중입니다'));
  };

  if (isLoading) {
    return (
      <AnimationPage>
        <CommonLoading minHeight={420} />
      </AnimationPage>
    );
  }

  const totalColSpan = config.columns.length;

  return (
    <AnimationPage>
      {/* 필터 행 — 표(내역 영역) 위에 분리 배치 (예약 내역 화면과 동일한 레이아웃) */}
      <FilterBar>
        <FilterGroup>
          <FilterLabel>{t('상품')}</FilterLabel>
          <CommonSelect
            value={productFilter}
            onChange={handleChangeProduct}
            emptyLabel={t('전체')}
            options={productOptions}
            width={220}
          />
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>{t('변경종류')}</FilterLabel>
          <CommonSelect
            value={actionFilter}
            onChange={handleChangeAction}
            emptyLabel={t('전체')}
            options={actionOptions}
            width={160}
          />
        </FilterGroup>

        <TotalCount>총 {filteredRows.length.toLocaleString()}건</TotalCount>
      </FilterBar>

      <Card elevation={2}>
        <ToolbarRow>
          <CommonExcelButton onClick={handleExcelDownload}>{t('전체 엑셀 다운로드')}</CommonExcelButton>
          <CommonSelect value={pageSize} onChange={handleChangePageSize} options={PAGE_SIZE_OPTIONS} width={110} />
        </ToolbarRow>

        <StyledTableContainer>
          <StyledTable size="small" aria-label="user course history table">
            <StyledTableHead>
              <BodyRow>
                {config.columns.map(col => (
                  <HeaderCell key={col.label} align="center">
                    {t(col.label)}
                  </HeaderCell>
                ))}
              </BodyRow>
            </StyledTableHead>

            <TableBody>
              {pagedRows.length === 0 ? (
                <BodyRow>
                  <EmptyCell colSpan={totalColSpan}>{t('내역이 없습니다')}</EmptyCell>
                </BodyRow>
              ) : (
                pagedRows.map((row, index) => (
                  <BodyRow key={row?.id ?? index}>
                    {config.columns.map(col => (
                      <BodyCell
                        key={col.label}
                        align="center"
                        wrap={!!col.wrap}
                        sx={col.preLine ? { whiteSpace: 'pre-line', wordBreak: 'keep-all', minWidth: 200 } : undefined}>
                        {col.render(row)}
                      </BodyCell>
                    ))}
                  </BodyRow>
                ))
              )}
            </TableBody>
          </StyledTable>
        </StyledTableContainer>

        <CommonPagination
          totalElements={filteredRows.length}
          limit={pageSize}
          page={page}
          onChange={handleChangePage}
        />
      </Card>
    </AnimationPage>
  );
};

export default UserCourseHistoryTable;
